'use client';

import { createContext, FC, PropsWithChildren, use, useMemo } from 'react';

import { useProducts } from '@/hooks';
import { CartLineItem } from '@/types';
import { resolveCartBadgeDealIds } from '@/utils/bogoSpecialCart';

import { useDealBundleStates } from '../../hooks/useBundleSpecial/useDealBundleStates';
import { DealBundleState } from '../../hooks/useBundleSpecial/bundleProgress';

/**
 * Cart-scoped source of per-deal completion state. It computes the state of every
 * deal present in the cart ONCE (one batch via useDealBundleStates) and exposes a
 * lookup. Each cart line's badge reads from here instead of re-classifying the cart
 * per line. Read-only — it never mutates cart lines.
 */
type CartDealStatesValue = {
  getDealState: (_dealId: string) => DealBundleState | undefined;
  isLoading: boolean;
};

const CartDealStatesContext = createContext<CartDealStatesValue>({
  getDealState: () => undefined,
  isLoading: false,
});
CartDealStatesContext.displayName = 'CartDealStatesContext';

const CartDealStatesProvider: FC<PropsWithChildren> = ({ children }) => {
  const { products } = useProducts();

  // All deals any cart line could badge: the line's bundle_id, or — for multi-deal
  // lines without a tag — every Pre-Cart deal on the line.
  const relevantDealIds = useMemo(() => {
    const ids = new Set<string>();

    (products ?? []).forEach((line: CartLineItem) => {
      resolveCartBadgeDealIds(line).forEach(id => {
        if (id) {
          ids.add(id);
        }
      });
    });

    return [...ids];
  }, [products]);

  const { dealStates, isLoading } = useDealBundleStates(relevantDealIds);

  const value = useMemo<CartDealStatesValue>(() => {
    const byId = new Map(dealStates.map(state => [state.specialId, state]));

    return {
      getDealState: (dealId: string) => byId.get(dealId),
      isLoading,
    };
  }, [dealStates, isLoading]);

  return (
    <CartDealStatesContext value={value}>{children}</CartDealStatesContext>
  );
};

const useCartDealStatesContext = () => use(CartDealStatesContext);

export { CartDealStatesProvider, useCartDealStatesContext };
